import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { appointmentsApi, leadsApi, patientsApi, eventsApi } from '../../services/api';
import { getCached } from '../../services/lookupCache';
import { APP_TIMEZONE } from '../../config/timezone';
import {
  formatDateTimeShortIST,
  formatYmdIST,
  todayYmdIST,
  formatHiMiIST,
  addMinutesOnWallClockIST,
} from '../../utils/timezoneDisplay';
import { usePermissions } from '../../hooks/usePermissions';
import { isForbidden } from '../../utils/accessDenied';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlineX, HiOutlineSearch } from 'react-icons/hi';
import { formatValidationErrors } from '../../utils/errorHandler';

const APPOINTMENT_TYPE_LABELS = {
  therapy: 'Therapy',
  assessment: 'Assessment',
  consultation: 'Consultation',
  follow_up: 'Follow-up',
  meeting: 'Meeting',
};

const isWorkshopEvent = (props) =>
  props?.type === 'event' || props?.type === 'training';

const isCalendarAppointment = (props) => props && !isWorkshopEvent(props);

const getAppointmentStatus = (props) =>
  props?.appointment_status ?? props?.status ?? 'scheduled';

const canManageAppointmentStatus = (status) =>
  ['scheduled', 'confirmed', 'in_progress'].includes(status);

const formatAppointmentStatusLabel = (status) =>
  (status || 'scheduled').replace(/_/g, ' ');

const normalizeTimePart = (time) => {
  if (!time) return '09:00:00';
  const str = String(time);
  return str.length === 5 ? `${str}:00` : str.slice(0, 8);
};

const normalizeDatePart = (date) => {
  if (!date) return todayYmdIST();
  if (typeof date === 'string') return date.split('T')[0];
  return formatYmdIST(new Date(date));
};

const buildCalendarEventFromAppointment = (apt, context = {}) => {
  const isLeadAppointment = apt.lead_id && !apt.patient_id;
  const isGeneralAppointment = !apt.patient_id && !apt.lead_id;
  const dateStr = apt.appointment_date_str || normalizeDatePart(apt.appointment_date);
  const startTimeStr = apt.start_time_str || normalizeTimePart(apt.start_time);
  const endTimeStr = apt.end_time_str || normalizeTimePart(apt.end_time);

  const getGeneralTitle = () => {
    const firstLine = apt.notes?.split('\n')[0]?.trim();
    if (firstLine) return firstLine;
    return APPOINTMENT_TYPE_LABELS[apt.type] || 'Appointment';
  };

  const title = isLeadAppointment
    ? `[Lead] ${apt.lead_name || context.lead_name || 'Lead'}`
    : (apt.patient_name || apt.lead_name || (isGeneralAppointment ? (context.generalTitle || getGeneralTitle()) : 'Appointment'));

  const status = apt.status || 'scheduled';

  return {
    id: apt.id,
    title,
    start: `${dateStr}T${startTimeStr}`,
    end: `${dateStr}T${endTimeStr}`,
    extendedProps: {
      patient_id: apt.patient_id,
      patient_name: apt.patient_name,
      patient_code: apt.patient_code,
      doctor_name: apt.doctor_name,
      type: apt.type,
      status,
      appointment_status: status,
      room: apt.room,
      notes: apt.notes,
      isLead: isLeadAppointment,
      isGeneral: isGeneralAppointment,
      lead_id: apt.lead_id,
      lead_name: apt.lead_name || context.lead_name,
      lead_status_id: apt.lead_status_id,
      lead_status_name: apt.lead_status_name,
      lead_status_color: apt.lead_status_color,
      contact: isLeadAppointment ? (apt.lead_phone || context.lead_phone) : apt.patient_phone,
      inquiry_type: apt.lead_inquiry_type || apt.type,
    },
  };
};

const Calendar = () => {
  const { canRead } = usePermissions();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patient_id');
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDateRange, setCurrentDateRange] = useState({ start: null, end: null });

  const fetchEvents = async (start, end) => {
    try {
      const params = {
        start_date: formatYmdIST(start),
        end_date: formatYmdIST(end),
      };
      if (patientId) {
        params.patient_id = patientId;
      }

      const eventsParams = {
        date_from: params.start_date,
        date_to: params.end_date
      };

      const jobs = [];
      if (canRead('appointments')) {
        jobs.push({ key: 'appts', run: () => appointmentsApi.getCalendar(params) });
      }
      if (canRead('events')) {
        jobs.push({ key: 'evts', run: () => eventsApi.getAll(eventsParams) });
        jobs.push({ key: 'train', run: () => eventsApi.getTrainingPrograms({}) });
      }

      if (jobs.length === 0) {
        setEvents([]);
        return;
      }

      const settled = await Promise.allSettled(jobs.map((j) => j.run()));

      let appointments = [];
      let eventsList = [];
      let trainings = [];

      settled.forEach((s, i) => {
        if (s.status !== 'fulfilled') {
          if (!isForbidden(s.reason)) {
            console.error('Calendar fetch error:', s.reason);
          }
          return;
        }
        const raw = s.value?.data?.data;
        const arr = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
        const key = jobs[i].key;
        if (key === 'appts') appointments = arr;
        else if (key === 'evts') eventsList = arr;
        else if (key === 'train') trainings = arr;
      });

      const anyNon403Failure = settled.some(
        (s) => s.status === 'rejected' && !isForbidden(s.reason)
      );
      if (anyNon403Failure) {
        toast.error('Failed to load some calendar data');
      }
      
      // Format appointments (already in correct format)
      const formattedAppointments = appointments;
      
      // Format events for FullCalendar
      const formattedEvents = eventsList.map(event => {
        const eventDate = new Date(event.event_date);
        const startTime = event.start_time ? event.start_time.split(':') : ['09', '00'];
        const endTime = event.end_time ? event.end_time.split(':') : ['10', '00'];
        
        const start = new Date(eventDate);
        start.setHours(parseInt(startTime[0]), parseInt(startTime[1]), 0);
        
        const end = new Date(eventDate);
        end.setHours(parseInt(endTime[0]), parseInt(endTime[1]), 0);
        
        return {
          id: `event-${event.id}`,
          title: event.title,
          start: start.toISOString(),
          end: end.toISOString(),
          backgroundColor: '#8B5CF6', // Purple for events
          borderColor: '#7C3AED',
          textColor: '#FFFFFF',
          extendedProps: {
            type: 'event',
            eventId: event.id,
            eventData: event,
            status: event.status,
            type_label: event.type,
            venue: event.venue,
            fee: event.fee
          }
        };
      });
      
      // Format training programs for FullCalendar
      // Filter training programs that fall within the date range
      const formattedTrainings = trainings
        .filter(training => {
          const trainingStart = new Date(training.start_date);
          const trainingEnd = training.end_date ? new Date(training.end_date) : trainingStart;
          // Include if training overlaps with the calendar date range
          return trainingEnd >= start && trainingStart <= end;
        })
        .map(training => {
          const startDate = new Date(training.start_date);
          const endDate = training.end_date ? new Date(training.end_date) : new Date(training.start_date);
          
          const startTime = training.start_time ? training.start_time.split(':') : ['09', '00'];
          const endTime = training.end_time ? training.end_time.split(':') : ['17', '00'];
          
          const start = new Date(startDate);
          start.setHours(parseInt(startTime[0]), parseInt(startTime[1]), 0);
          
          const end = new Date(endDate);
          end.setHours(parseInt(endTime[0]), parseInt(endTime[1]), 0);
          
          return {
            id: `training-${training.id}`,
            title: training.title,
            start: start.toISOString(),
            end: end.toISOString(),
            backgroundColor: '#F59E0B', // Amber for training programs
            borderColor: '#D97706',
            textColor: '#FFFFFF',
            extendedProps: {
              type: 'training',
              trainingId: training.id,
              trainingData: training,
              status: training.status,
              mode: training.mode,
              venue: training.venue,
              fee: training.fee
            }
          };
        });
      
      // Combine all events
      const allEvents = [...formattedAppointments, ...formattedEvents, ...formattedTrainings];
      setEvents(allEvents);
    } catch (error) {
      if (!isForbidden(error)) {
        console.error('Error fetching calendar events:', error);
        toast.error('Failed to load calendar events');
      } else {
        setEvents([]);
      }
    }
  };

  const formatTime = (date) => formatHiMiIST(date);

  const getCurrentTime = () => formatHiMiIST(new Date());

  const add30Minutes = (timeStr, dateYmd) =>
    addMinutesOnWallClockIST(dateYmd || todayYmdIST(), timeStr, 30);

  const handleDateClick = (arg) => {
    const clickedDate = arg.date;
    const dateStr = formatYmdIST(clickedDate);

    const startTime = formatTime(clickedDate);

    const endTime = add30Minutes(startTime, dateStr);
    
    setSelectedDate(dateStr);
    setSelectedStartTime(startTime);
    setSelectedEndTime(endTime);
    setSelectedEvent(null);
    setShowModal(true);
  };

  const handleEventClick = (arg) => {
    setSelectedEvent(arg.event);
    setShowModal(true);
  };

  const handleDatesSet = (arg) => {
    setCurrentDateRange({ start: arg.start, end: arg.end });
    fetchEvents(arg.start, arg.end);
  };

  const refreshEvents = () => {
    if (currentDateRange.start && currentDateRange.end) {
      fetchEvents(currentDateRange.start, currentDateRange.end);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Calendar</h1>
          <p className="text-gray-500 mt-1">Manage appointments and schedules</p>
          {!canRead('appointments') && !canRead('events') && (
            <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              You do not have permission to view appointments or events on the calendar. Ask an administrator
              to enable <strong>appointments</strong> and/or <strong>events</strong> read access for your role.
            </p>
          )}
          {canRead('appointments') && !canRead('events') && (
            <p className="mt-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Workshops, training programs, and clinic events are hidden because your role does not include{' '}
              <strong>events</strong> read access.
            </p>
          )}
          {!canRead('appointments') && canRead('events') && (
            <p className="mt-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Patient appointments are hidden because your role does not include <strong>appointments</strong>{' '}
              read access.
            </p>
          )}
        </div>
        <button
          onClick={() => {
            const today = todayYmdIST();
            const currentTime = getCurrentTime();
            const endTime = add30Minutes(currentTime, today);
            setSelectedDate(today);
            setSelectedStartTime(currentTime);
            setSelectedEndTime(endTime);
            setSelectedEvent(null);
            setShowModal(true);
          }}
          className="btn btn-primary"
        >
          <HiOutlinePlus className="w-5 h-5 mr-2" />
          New Appointment
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 items-center text-sm">
        <span className="text-gray-500 font-medium">Legend:</span>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#8B5CF6' }}></span>
          <span className="text-gray-600">Events</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#F59E0B' }}></span>
          <span className="text-gray-600">Training Programs</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#F59E0B' }}></span>
          <span className="text-gray-600">Lead Appointments</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#6366F1' }}></span>
          <span className="text-gray-600">General Appointments</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }}></span>
          <span className="text-gray-600">Scheduled</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#10B981' }}></span>
          <span className="text-gray-600">Confirmed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#059669' }}></span>
          <span className="text-gray-600">Completed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }}></span>
          <span className="text-gray-600">Cancelled</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#6B7280' }}></span>
          <span className="text-gray-600">No Show</span>
        </div>
      </div>

      {/* Calendar */}
      <div className="card">
        <FullCalendar
          timeZone={APP_TIMEZONE}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={events}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          allDaySlot={false}
          height="auto"
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            meridiem: 'short'
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            meridiem: 'short'
          }}
        />
      </div>

      {/* Appointment Modal */}
      {showModal && (
        <AppointmentModal
          date={selectedDate}
          startTime={selectedStartTime}
          endTime={selectedEndTime}
          event={selectedEvent}
          onClose={() => {
            setShowModal(false);
            setSelectedEvent(null);
            setSelectedStartTime(null);
            setSelectedEndTime(null);
          }}
          onSuccess={(result) => {
            refreshEvents();
            if (result?.mode === 'created' && result.appointment) {
              const built = buildCalendarEventFromAppointment(
                result.appointment,
                result.formContext || {}
              );
              setSelectedEvent(built);
              return;
            }
            setShowModal(false);
            setSelectedEvent(null);
            setSelectedStartTime(null);
            setSelectedEndTime(null);
          }}
        />
      )}
    </div>
  );
};

const AppointmentModal = ({ date, startTime, endTime, event, onClose, onSuccess }) => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [sourceType, setSourceType] = useState('general'); // 'general', 'lead', or 'patient'
  const [generalTitle, setGeneralTitle] = useState('');
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    new_date: '',
    new_start_time: '',
    new_end_time: '',
    reason: ''
  });
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const getCurrentTime = () => formatHiMiIST(new Date());

  const add30Minutes = (timeStr, dateYmd) =>
    addMinutesOnWallClockIST(dateYmd || todayYmdIST(), timeStr, 30);

  // Initialize form data with default values
  const getInitialFormData = () => {
    const today = todayYmdIST();
    const selectedDate = date || today;
    const isToday = selectedDate === today;

    let defaultStartTime = '09:00';
    if (startTime) {
      defaultStartTime = startTime;
    } else if (isToday) {
      defaultStartTime = getCurrentTime();
    }

    let defaultEndTime = '09:30';
    if (endTime) {
      defaultEndTime = endTime;
    } else {
      defaultEndTime = add30Minutes(defaultStartTime, selectedDate);
    }

    return {
      patient_id: '',
      lead_id: '',
      doctor_id: '',
      appointment_date: selectedDate,
      start_time: defaultStartTime,
      end_time: defaultEndTime,
      type: 'therapy',
      room: '',
      notes: ''
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Appointment/Inquiry types (same options)
  const appointmentTypes = [
    { value: 'therapy', label: 'Therapy' },
    { value: 'assessment', label: 'Assessment' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'special_case', label: 'Special Case' },
    { value: 'follow_up', label: 'Follow-up' }
  ];

  // Update formData when date, startTime, or endTime changes
  useEffect(() => {
    if (date || startTime || endTime) {
      setFormData(prev => {
        const today = todayYmdIST();
        const aptDate = date || prev.appointment_date || today;
        const newStartTime =
          startTime || (aptDate === today ? formatHiMiIST(new Date()) : prev.start_time);
        const newEndTime = endTime || add30Minutes(newStartTime, aptDate);
        return {
          ...prev,
          appointment_date: aptDate,
          start_time: newStartTime,
          end_time: newEndTime,
        };
      });
    }
  }, [date, startTime, endTime]);

  // Fetch lead statuses when viewing a lead appointment
  useEffect(() => {
    if (event?.extendedProps?.isLead && leadStatuses.length === 0) {
      const fetchStatuses = async () => {
        try {
          const response = await getCached('leads:statuses', () => leadsApi.getStatuses());
          setLeadStatuses(response.data.data || []);
        } catch (error) {
          console.error('Error fetching lead statuses:', error);
        }
      };
      fetchStatuses();
    }
  }, [event, leadStatuses.length]);

  useEffect(() => {
    if (event?.id) {
      setRescheduleMode(false);
      setShowCompleteConfirm(false);
      setShowCancelModal(false);
      setShowDeleteModal(false);
      setCancelReason('');
    }
  }, [event?.id]);

  // Search leads
  useEffect(() => {
    const searchLeads = async () => {
      if (searchTerm.length < 2) {
        setLeads([]);
        return;
      }
      setSearching(true);
      try {
        const response = await leadsApi.getForDropdown({ search: searchTerm });
        setLeads(response.data.data || []);
      } catch (error) {
        console.error('Error searching leads:', error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchLeads, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  // Search patients
  useEffect(() => {
    const searchPatients = async () => {
      if (patientSearchTerm.length < 2) {
        setPatients([]);
        return;
      }
      setSearchingPatients(true);
      try {
        const response = await patientsApi.getAll({ 
          search: patientSearchTerm,
          limit: 20,
          page: 1
        });
        setPatients(response.data.data.patients || []);
      } catch (error) {
        console.error('Error searching patients:', error);
      } finally {
        setSearchingPatients(false);
      }
    };

    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [patientSearchTerm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLeadSelect = (lead) => {
    setFormData({
      ...formData,
      lead_id: lead.id,
      patient_id: '',
      type: lead.inquiry_type || 'therapy'
    });
    setSearchTerm(lead.full_name);
    setLeads([]);
  };

  const handlePatientSelect = (patient) => {
    setFormData({
      ...formData,
      patient_id: patient.id,
      lead_id: '',
      type: 'therapy'
    });
    setPatientSearchTerm(patient.full_name);
    setPatients([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate that appointment is not in the past (only for new appointments)
      if (!event) {
        // Parse appointment date (YYYY-MM-DD format)
        const [year, month, day] = formData.appointment_date.split('-').map(Number);
        const appointmentDateOnly = new Date(year, month - 1, day);
        
        // Get today's date (reset time to midnight for comparison)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Compare dates first - allow today and future dates
        if (appointmentDateOnly < today) {
          toast.error('Cannot create appointments in the past. Please select today or a future date.');
          setLoading(false);
          return;
        }
        
        // If date is today, check if time is in the future
        if (appointmentDateOnly.getTime() === today.getTime()) {
          const [hours, minutes] = formData.start_time.split(':').map(Number);
          const appointmentDateTime = new Date(year, month - 1, day, hours, minutes);
          const now = new Date();
          
          if (appointmentDateTime <= now) {
            toast.error('Cannot create appointments in the past. Please select a future time for today or a future date.');
            setLoading(false);
            return;
          }
        }
      }

      let notes = formData.notes;
      if (!event && sourceType === 'general' && generalTitle.trim()) {
        notes = generalTitle.trim() + (formData.notes?.trim() ? `\n\n${formData.notes.trim()}` : '');
      }

      const payload = {
        ...formData,
        patient_id: sourceType === 'patient' && formData.patient_id ? formData.patient_id : null,
        lead_id: sourceType === 'lead' && formData.lead_id ? formData.lead_id : null,
        doctor_id: formData.doctor_id || null,
        notes,
      };

      if (event) {
        await appointmentsApi.update(event.id, payload);
        toast.success('Appointment updated');
        onSuccess();
      } else {
        const response = await appointmentsApi.create(payload);
        toast.success('Appointment created');
        const selectedLead = leads.find((l) => l.id === formData.lead_id);
        onSuccess({
          mode: 'created',
          appointment: response.data.data,
          formContext: {
            generalTitle: sourceType === 'general' ? generalTitle.trim() : '',
            lead_name: selectedLead?.full_name || searchTerm,
            lead_phone: selectedLead?.contact_primary,
          },
        });
      }
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteClick = () => {
    setShowCompleteConfirm(true);
  };

  const handleCompleteConfirm = async () => {
    if (!event) return;

    setLoading(true);
    setShowCompleteConfirm(false);
    try {
      await appointmentsApi.updateStatus(event.id, { status: 'completed' });
      toast.success('Appointment marked as completed');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!event) return;

    setLoading(true);
    setShowCancelModal(false);
    try {
      await appointmentsApi.cancel(event.id, { reason: cancelReason || 'Cancelled by user' });
      toast.success('Appointment cancelled');
      setCancelReason('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!event) return;

    setLoading(true);
    setShowDeleteModal(false);
    try {
      await appointmentsApi.delete(event.id);
      toast.success('Appointment deleted');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    if (!event) return;

    // Validate that rescheduled appointment is not in the past
    const [year, month, day] = rescheduleData.new_date.split('-').map(Number);
    const rescheduledDateOnly = new Date(year, month - 1, day);
    
    // Get today's date (reset time to midnight for comparison)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Compare dates first - allow today and future dates
    if (rescheduledDateOnly < today) {
      toast.error('Cannot reschedule to a past date. Please select today or a future date.');
      return;
    }
    
    // If date is today, check if time is in the future
    if (rescheduledDateOnly.getTime() === today.getTime()) {
      const [hours, minutes] = rescheduleData.new_start_time.split(':').map(Number);
      const rescheduledDateTime = new Date(year, month - 1, day, hours, minutes);
      const now = new Date();
      
      if (rescheduledDateTime <= now) {
        toast.error('Cannot reschedule to a past time. Please select a future time for today or a future date.');
        return;
      }
    }

    setLoading(true);
    try {
      await appointmentsApi.reschedule(event.id, rescheduleData);
      toast.success('Appointment rescheduled');
      setRescheduleMode(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reschedule appointment');
    } finally {
      setLoading(false);
    }
  };

  // Initialize reschedule data when event is available
  useEffect(() => {
    if (event && rescheduleMode) {
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);
      setRescheduleData({
        new_date: formatYmdIST(startDate),
        new_start_time: formatHiMiIST(startDate),
        new_end_time: formatHiMiIST(endDate),
        reason: '',
      });
    }
  }, [event, rescheduleMode]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {event 
                ? event.extendedProps?.type === 'event' 
                  ? 'Event Details' 
                  : event.extendedProps?.type === 'training'
                  ? 'Training Program Details'
                  : event.extendedProps?.isLead && event.extendedProps?.notes?.includes('Follow-up')
                  ? 'Lead Follow-up'
                  : 'Appointment Details'
                : 'New Appointment'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <HiOutlineX className="w-5 h-5" />
            </button>
          </div>

          {event ? (
            <div className="p-6 space-y-4">
              {/* Check if it's an event */}
              {event.extendedProps?.type === 'event' ? (
                <>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-medium text-purple-800">Event</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Event Title</p>
                    <p className="font-medium">{event.title}</p>
                  </div>
                  {event.extendedProps?.eventData?.description && (
                    <div>
                      <p className="text-sm text-gray-500">Description</p>
                      <p className="text-gray-700">{event.extendedProps.eventData.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">
                      {formatDateTimeShortIST(new Date(event.start))}
                      {event.end && ` - ${formatDateTimeShortIST(new Date(event.end))}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`badge ${
                      event.extendedProps?.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                      event.extendedProps?.status === 'completed' ? 'bg-green-100 text-green-700' :
                      event.extendedProps?.status === 'ongoing' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {event.extendedProps?.status || 'upcoming'}
                    </span>
                  </div>
                  {event.extendedProps?.type_label && (
                    <div>
                      <p className="text-sm text-gray-500">Type</p>
                      <span className="badge bg-purple-100 text-purple-700 capitalize">
                        {event.extendedProps.type_label}
                      </span>
                    </div>
                  )}
                  {event.extendedProps?.venue && (
                    <div>
                      <p className="text-sm text-gray-500">Venue</p>
                      <p className="font-medium">{event.extendedProps.venue}</p>
                    </div>
                  )}
                  {event.extendedProps?.fee > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">Fee</p>
                      <p className="font-medium">₹{parseFloat(event.extendedProps.fee).toFixed(2)}</p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    {event.extendedProps?.status !== 'completed' && (
                      <>
                        <button
                          onClick={async () => {
                            setLoading(true);
                            try {
                              await eventsApi.update(event.extendedProps.eventId, { status: 'completed' });
                              toast.success('Event marked as completed');
                              onSuccess();
                            } catch (error) {
                              toast.error(formatValidationErrors(error));
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="btn btn-success flex-1"
                          disabled={loading}
                        >
                          {loading ? 'Updating...' : 'Mark as Completed'}
                        </button>
                        {event.extendedProps?.status === 'upcoming' && (
                          <button
                            onClick={async () => {
                              setLoading(true);
                              try {
                                await eventsApi.update(event.extendedProps.eventId, { status: 'ongoing' });
                                toast.success('Event marked as ongoing');
                                onSuccess();
                              } catch (error) {
                                toast.error(formatValidationErrors(error));
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="btn btn-secondary flex-1"
                            disabled={loading}
                          >
                            {loading ? 'Updating...' : 'Mark as Ongoing'}
                          </button>
                        )}
                      </>
                    )}
                    {event.extendedProps?.status === 'completed' && (
                      <button
                        onClick={onClose}
                        className="btn btn-secondary flex-1"
                      >
                        Close
                      </button>
                    )}
                  </div>
                </>
              ) : event.extendedProps?.type === 'training' ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-medium text-amber-800">Training Program</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Program Title</p>
                    <p className="font-medium">{event.title}</p>
                  </div>
                  {event.extendedProps?.trainingData?.description && (
                    <div>
                      <p className="text-sm text-gray-500">Description</p>
                      <p className="text-gray-700">{event.extendedProps.trainingData.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Start Date & Time</p>
                    <p className="font-medium">
                      {formatDateTimeShortIST(new Date(event.start))}
                    </p>
                  </div>
                  {event.end && (
                    <div>
                      <p className="text-sm text-gray-500">End Date & Time</p>
                      <p className="font-medium">
                        {formatDateTimeShortIST(new Date(event.end))}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`badge ${
                      event.extendedProps?.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                      event.extendedProps?.status === 'completed' ? 'bg-green-100 text-green-700' :
                      event.extendedProps?.status === 'ongoing' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {event.extendedProps?.status || 'upcoming'}
                    </span>
                  </div>
                  {event.extendedProps?.mode && (
                    <div>
                      <p className="text-sm text-gray-500">Mode</p>
                      <p className="font-medium capitalize">{event.extendedProps.mode}</p>
                    </div>
                  )}
                  {event.extendedProps?.venue && (
                    <div>
                      <p className="text-sm text-gray-500">Venue</p>
                      <p className="font-medium">{event.extendedProps.venue}</p>
                    </div>
                  )}
                  {event.extendedProps?.fee > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">Fee</p>
                      <p className="font-medium">₹{parseFloat(event.extendedProps.fee).toFixed(2)}</p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    {event.extendedProps?.status !== 'completed' && (
                      <>
                        <button
                          onClick={async () => {
                            setLoading(true);
                            try {
                              await eventsApi.updateTrainingProgram(event.extendedProps.trainingId, { status: 'completed' });
                              toast.success('Training program marked as completed');
                              onSuccess();
                            } catch (error) {
                              toast.error(formatValidationErrors(error));
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="btn btn-success flex-1"
                          disabled={loading}
                        >
                          {loading ? 'Updating...' : 'Mark as Completed'}
                        </button>
                        {event.extendedProps?.status === 'upcoming' && (
                          <button
                            onClick={async () => {
                              setLoading(true);
                              try {
                                await eventsApi.updateTrainingProgram(event.extendedProps.trainingId, { status: 'ongoing' });
                                toast.success('Training program marked as ongoing');
                                onSuccess();
                              } catch (error) {
                                toast.error(formatValidationErrors(error));
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="btn btn-secondary flex-1"
                            disabled={loading}
                          >
                            {loading ? 'Updating...' : 'Mark as Ongoing'}
                          </button>
                        )}
                      </>
                    )}
                    {event.extendedProps?.status === 'completed' && (
                      <button
                        onClick={onClose}
                        className="btn btn-secondary flex-1"
                      >
                        Close
                      </button>
                    )}
                  </div>
                </>
              ) : event.extendedProps?.isLead ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-medium text-amber-800">
                      {event.extendedProps?.notes?.includes('Follow-up') ? 'Lead Follow-up' : 'Lead Appointment'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Lead Name</p>
                    <p className="font-medium">{event.extendedProps?.lead_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-medium">{event.extendedProps?.contact}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Inquiry Type</p>
                    <span className="badge bg-amber-100 text-amber-700 capitalize">
                      {event.extendedProps?.inquiry_type?.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">
                      {formatDateTimeShortIST(new Date(event.start))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Appointment Status</p>
                    <span className="badge bg-primary-100 text-primary-700 capitalize">
                      {formatAppointmentStatusLabel(getAppointmentStatus(event.extendedProps))}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1.5">Lead Status</p>
                    {leadStatuses.length > 0 ? (
                      <select
                        value={event.extendedProps?.lead_status_id || ''}
                        onChange={async (e) => {
                          const newStatusId = parseInt(e.target.value);
                          if (!newStatusId || !event.extendedProps?.lead_id) return;
                          
                          setUpdatingStatus(true);
                          try {
                            // Check if the new status requires follow-up
                            const newStatus = leadStatuses.find(s => s.id === newStatusId);
                            const requiresFollowUp = newStatus && ['Follow-up', 'DNP'].includes(newStatus.name);
                            
                            if (requiresFollowUp) {
                              toast.error('Please update status from Leads page to set follow-up date');
                              e.target.value = event.extendedProps?.lead_status_id || '';
                              setUpdatingStatus(false);
                              return;
                            }
                            
                            await leadsApi.updateStatus(event.extendedProps.lead_id, { status_id: newStatusId });
                            toast.success('Lead status updated successfully');
                            onSuccess(); // Refresh calendar
                          } catch (error) {
                            toast.error(error.response?.data?.message || 'Failed to update lead status');
                            e.target.value = event.extendedProps?.lead_status_id || '';
                          } finally {
                            setUpdatingStatus(false);
                          }
                        }}
                        className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer w-full"
                        disabled={updatingStatus}
                        style={{ 
                          color: leadStatuses.find(s => s.id === event.extendedProps?.lead_status_id)?.color || '#374151',
                          backgroundColor: 'white'
                        }}
                      >
                        {leadStatuses.map(status => (
                          <option key={status.id} value={status.id} style={{ color: status.color }}>
                            {status.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-700">
                        {event.extendedProps?.lead_status_name || 'Loading...'}
                      </span>
                    )}
                    {updatingStatus && (
                      <p className="text-xs text-gray-500 mt-1">Updating status...</p>
                    )}
                  </div>
                </>
              ) : event.extendedProps?.isGeneral ? (
                <>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-medium text-indigo-800">General Appointment</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Title</p>
                    <p className="font-medium">{event.title}</p>
                  </div>
                  {event.extendedProps?.type && (
                    <div>
                      <p className="text-sm text-gray-500">Type</p>
                      <span className="badge bg-indigo-100 text-indigo-700 capitalize">
                        {event.extendedProps.type.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">
                      {formatDateTimeShortIST(new Date(event.start))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Appointment Status</p>
                    <span className="badge bg-primary-100 text-primary-700 capitalize">
                      {formatAppointmentStatusLabel(getAppointmentStatus(event.extendedProps))}
                    </span>
                  </div>
                  {event.extendedProps?.room && (
                    <div>
                      <p className="text-sm text-gray-500">Room</p>
                      <p className="font-medium">{event.extendedProps.room}</p>
                    </div>
                  )}
                  {event.extendedProps?.notes && (
                    <div>
                      <p className="text-sm text-gray-500">Notes</p>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {event.extendedProps.notes.includes('\n\n')
                          ? event.extendedProps.notes.split('\n\n').slice(1).join('\n\n')
                          : event.extendedProps.notes}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Patient</p>
                    <p className="font-medium">{event.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">
                      {formatDateTimeShortIST(new Date(event.start))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className="badge bg-primary-100 text-primary-700 capitalize">
                      {formatAppointmentStatusLabel(getAppointmentStatus(event.extendedProps))}
                    </span>
                  </div>
                  {event.extendedProps?.doctor_name && (
                    <div>
                      <p className="text-sm text-gray-500">Doctor</p>
                      <p className="font-medium">{event.extendedProps.doctor_name}</p>
                    </div>
                  )}
                </>
              )}
              
              {/* Reschedule Form - Only for appointments */}
              {isCalendarAppointment(event.extendedProps) && rescheduleMode && canUpdate('appointments') ? (
                <form onSubmit={handleRescheduleSubmit} className="pt-4 border-t border-gray-200 space-y-4">
                  <h3 className="font-semibold text-gray-900">Reschedule Appointment</h3>
                  <div>
                    <label className="label">New Date *</label>
                    <input
                      type="date"
                      value={rescheduleData.new_date}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, new_date: e.target.value })}
                      className="input"
                      min={todayYmdIST()}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">New Start Time *</label>
                      <input
                        type="time"
                        value={rescheduleData.new_start_time}
                        onChange={(e) => setRescheduleData({ ...rescheduleData, new_start_time: e.target.value })}
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="label">New End Time *</label>
                      <input
                        type="time"
                        value={rescheduleData.new_end_time}
                        onChange={(e) => setRescheduleData({ ...rescheduleData, new_end_time: e.target.value })}
                        className="input"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label">Reason</label>
                    <textarea
                      value={rescheduleData.reason}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, reason: e.target.value })}
                      className="input"
                      rows={2}
                      placeholder="Reason for rescheduling..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRescheduleMode(false)}
                      className="btn btn-secondary flex-1"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary flex-1"
                      disabled={loading}
                    >
                      {loading ? 'Rescheduling...' : 'Reschedule'}
                    </button>
                  </div>
                </form>
              ) : isCalendarAppointment(event.extendedProps) ? (
                <div className="pt-4 border-t border-gray-200">
                  {(() => {
                    const appointmentStatus = getAppointmentStatus(event.extendedProps);
                    const canManage = canManageAppointmentStatus(appointmentStatus);

                    if (canManage && canUpdate('appointments')) {
                      return (
                        <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-2">
                          <button
                            onClick={handleCompleteClick}
                            className="btn btn-success flex-1 min-w-0 whitespace-nowrap px-2 sm:px-3 text-xs sm:text-sm shadow-sm"
                            disabled={loading}
                          >
                            Complete
                          </button>
                          <button
                            onClick={() => setRescheduleMode(true)}
                            className="btn btn-secondary flex-1 min-w-0 whitespace-nowrap px-2 sm:px-3 text-xs sm:text-sm"
                            disabled={loading}
                          >
                            Reschedule
                          </button>
                          <button
                            onClick={handleCancelClick}
                            className="btn btn-warning-outline flex-1 min-w-0 whitespace-nowrap px-2 sm:px-3 text-xs sm:text-sm"
                            disabled={loading}
                          >
                            Cancel
                          </button>
                          {canDelete('appointments') && (
                            <button
                              onClick={handleDeleteClick}
                              className="btn btn-danger flex-1 min-w-0 whitespace-nowrap px-2 sm:px-3 text-xs sm:text-sm shadow-sm"
                              disabled={loading}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <button
                        onClick={onClose}
                        className="btn btn-secondary w-full"
                      >
                        Close
                      </button>
                    );
                  })()}
                </div>
              ) : null}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Source Type Tabs */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => {
                    setSourceType('general');
                    setFormData({ ...formData, patient_id: '', lead_id: '' });
                    setSearchTerm('');
                    setPatientSearchTerm('');
                  }}
                  className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition-colors ${
                    sourceType === 'general'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  General
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSourceType('lead');
                    setFormData({ ...formData, patient_id: '', lead_id: '' });
                    setSearchTerm('');
                    setPatientSearchTerm('');
                    setGeneralTitle('');
                  }}
                  className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition-colors ${
                    sourceType === 'lead'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  From Lead
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSourceType('patient');
                    setFormData({ ...formData, patient_id: '', lead_id: '' });
                    setSearchTerm('');
                    setPatientSearchTerm('');
                    setGeneralTitle('');
                  }}
                  className={`flex-1 py-2 px-2 rounded-md text-sm font-medium transition-colors ${
                    sourceType === 'patient'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  From Patient
                </button>
              </div>

              {sourceType === 'general' && (
                <div>
                  <label className="label">Title (optional)</label>
                  <input
                    type="text"
                    value={generalTitle}
                    onChange={(e) => setGeneralTitle(e.target.value)}
                    className="input"
                    placeholder="e.g. Team meeting, room booking..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    No lead or patient required. Shown on the calendar when provided.
                  </p>
                </div>
              )}

              {/* Lead Search */}
              {sourceType === 'lead' && (
                <div className="relative">
                  <label className="label">Select Lead (optional)</label>
                  <div className="relative">
                    <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input pl-10"
                      placeholder="Search by name or phone..."
                    />
                  </div>
                  {searching && (
                    <p className="text-xs text-gray-500 mt-1">Searching...</p>
                  )}
                  {leads.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {leads.map((lead) => (
                        <button
                          key={lead.id}
                          type="button"
                          onClick={() => handleLeadSelect(lead)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <p className="font-medium text-gray-900">{lead.full_name}</p>
                          <p className="text-xs text-gray-500">
                            {lead.contact_primary} • {lead.inquiry_type} • {lead.status_name}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                  {formData.lead_id && (
                    <p className="text-xs text-emerald-600 mt-1">✓ Lead selected</p>
                  )}
                </div>
              )}

              {/* Patient Search */}
              {sourceType === 'patient' && (
                <div className="relative">
                  <label className="label">Select Patient (optional)</label>
                  <div className="relative">
                    <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={patientSearchTerm}
                      onChange={(e) => setPatientSearchTerm(e.target.value)}
                      className="input pl-10"
                      placeholder="Search by name, patient ID, or phone number..."
                    />
                  </div>
                  {searchingPatients && (
                    <p className="text-xs text-gray-500 mt-1">Searching...</p>
                  )}
                  {patients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {patients.map((patient) => (
                        <button
                          key={patient.id}
                          type="button"
                          onClick={() => handlePatientSelect(patient)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <p className="font-medium text-gray-900">{patient.full_name}</p>
                          <p className="text-xs text-gray-500">
                            {patient.patient_code} • {patient.contact_primary} • {patient.status || 'Active'}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                  {formData.patient_id && (
                    <p className="text-xs text-emerald-600 mt-1">✓ Patient selected</p>
                  )}
                </div>
              )}

              <div>
                <label className="label">Date *</label>
                <input
                  type="date"
                  name="appointment_date"
                  value={formData.appointment_date}
                  onChange={handleChange}
                  className="input"
                  min={todayYmdIST()}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Start Time *</label>
                  <input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">End Time *</label>
                  <input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="label">Appointment Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="input"
                >
                  {appointmentTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Room</label>
                <input
                  type="text"
                  name="room"
                  value={formData.room}
                  onChange={handleChange}
                  className="input"
                  placeholder="Room 1"
                />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
                  Cancel
                </button>
                {canCreate('appointments') && (
                  <button type="submit" disabled={loading} className="btn btn-primary flex-1">
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Complete Confirmation Modal */}
      {showCompleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowCompleteConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Mark as Completed</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                Are you sure you want to mark this appointment as completed?
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCompleteConfirm(false)}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteConfirm}
                className="btn btn-success"
                disabled={loading}
              >
                {loading ? 'Completing...' : 'Mark as Completed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Appointment Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Delete Appointment</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                Are you sure you want to permanently delete this appointment? This cannot be undone.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="btn btn-danger"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Appointment Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowCancelModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Appointment</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700">
                Are you sure you want to cancel this appointment?
              </p>
              <div>
                <label className="label">Cancellation Reason (Optional)</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Please provide a reason for cancellation..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleCancelConfirm}
                className="btn btn-warning-outline"
                disabled={loading}
              >
                {loading ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;

